import React, { useEffect, useState, useCallback } from "react";
import styled from "@emotion/styled";

import Portal from "../Portal";


const FALLBACK_ID = "feedbackBar";
const FALLBACK_ACTIVE_CLASS = "active";
const WAIT_TIME_SUCCESS = 2000;
const WAIT_TIME_ERROR = 3000;

const Wrapper = styled.div`
  position: fixed;
  z-index: 10;
  bottom: 15px;
  left: 0;
  right: 0;
  background: linear-gradient(0deg, rgba(101, 119, 134, 0.16), rgba(101, 119, 134, 0.16)), linear-gradient(0deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03)), #0E0F10;
  box-shadow: 0px 4px 8px rgba(0, 19, 37, 0.24);
  border-radius: 8px;
  color: #fff;  
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.5px;
  width: calc(100% - 30px);
  margin : 0 auto;

  .container {
    min-height: 55px;
    padding: 10px 20px;
    margin-bottom: -80px;
    transition: all .2s;

    &.${FALLBACK_ACTIVE_CLASS} {
      margin-bottom: 0;
    }

    .body {
      flex: 1;
    }
  }
`;


/**
 * @description : This component will be used for showing API response and API status to users.
 *                Whether the action you have performed is correct or not.
 * @param : content : Message to show
 * @param : autoClose : Will decide whether feedbackBar will close automatically after a
 *                      particular interval
 * @param : closeCallback : Take a callabck to be called when feedbackBar is closed.
 * @param : errorType : It will decide whether to show error view (Try Again) or normal view.
 * @param : feedbackBarId : A unique Id for every feedbackBar
 */
interface FeedbackBarProps {
  content: (string | React.ReactNode);
  autoClose?: boolean;
  successCloseCallback: Function;
  errorCloseCallback: Function;
  errorType?: boolean;
  feedbackBarId: string;
}

function FeedbackBar({
  content,
  autoClose,
  successCloseCallback,
  errorCloseCallback,
  errorType = false,
  feedbackBarId = FALLBACK_ID,
}: FeedbackBarProps) {
  const [isMounted, setIsMounted] = useState(false);

  const handleMount = useCallback(() => {
    if (isMounted) {
      const feedbackBarNode = document.getElementById(feedbackBarId);
      if (feedbackBarNode) {
        feedbackBarNode.classList.add(FALLBACK_ACTIVE_CLASS);
      }
    }
  }, [isMounted, feedbackBarId]);

  const handleClose = useCallback((fromTryAgain, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (isMounted) {
      const feedbackBarNode = document.getElementById(feedbackBarId);
      if (feedbackBarNode) {
        if (autoClose && !fromTryAgain) {
          if (errorCloseCallback && errorType) {
            errorCloseCallback();
          } else if (successCloseCallback) {
          successCloseCallback();
          }
          feedbackBarNode.classList.remove(FALLBACK_ACTIVE_CLASS);
        } else {
          errorCloseCallback();
        }
      }
    }
  }, [errorType, isMounted, feedbackBarId, errorCloseCallback, successCloseCallback, autoClose]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (content) {
      handleMount();
    }
  }, [content, handleMount]);

  useEffect(() => {
    if (autoClose) {
      setTimeout(() => {
        handleClose(false, null);
      }, errorType ? WAIT_TIME_ERROR : WAIT_TIME_SUCCESS);
    }
  }, [autoClose, handleClose, errorType]);

  return (
    <Portal>
      <Wrapper>
        <div className="container layout horizontal center height-full" id={feedbackBarId}>
          <div className="body">
            <p>{content}</p>
            {/* {errorType && <TryAgainButton onClick={(e) => handleClose(true, e)}>Try Again</TryAgainButton>} */}
          </div>
          {
            (!autoClose) && (
              <button
                type="button"
                className="close-snackbar"
                onClick={(e) => handleClose(false, e)}
              >
                &#10006;
              </button>
            )
          }
        </div>
      </Wrapper>
    </Portal>
  );
}

export default FeedbackBar;
